/**
 * HelpIllustrationEmbed
 *
 * Renders an illustration from the registry with proper loading states
 * and accessibility support. Used by MarkdownWithVisuals.
 */

import { Suspense } from 'react';
import { Box, SkeletonDisplayText, Text, BlockStack } from '@shopify/polaris';
import {
  getIllustration,
  getIllustrationMeta,
  type IllustrationName,
  type IllustrationProps,
} from './illustrations';

interface HelpIllustrationEmbedProps {
  /** Name of the illustration to render */
  name: string;
  /** Optional caption text */
  caption?: string;
  /** Override default width */
  width?: number | string;
  /** Override default height */
  height?: number | string;
  /** Additional CSS class */
  className?: string;
}

/** Loading skeleton for illustrations */
function IllustrationSkeleton({
  width,
  height,
}: {
  width: number;
  height: number;
}) {
  return (
    <div
      className="help-illustration-skeleton"
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        maxWidth: '100%',
      }}
    >
      <SkeletonDisplayText size="small" />
    </div>
  );
}

/** Error fallback when illustration fails to load */
function IllustrationError({ name }: { name: string }) {
  return (
    <Box
      background="bg-surface-secondary"
      padding="400"
      borderRadius="200"
      borderColor="border"
      borderWidth="025"
    >
      <Text tone="subdued" as="p">
        Illustration not available: {name}
      </Text>
    </Box>
  );
}

export function HelpIllustrationEmbed({
  name,
  caption,
  width,
  height,
  className,
}: HelpIllustrationEmbedProps) {
  const Illustration = getIllustration(name);
  const meta = getIllustrationMeta(name);

  if (!Illustration || !meta) {
    return <IllustrationError name={name} />;
  }

  const resolvedWidth = width ?? meta.defaultWidth;
  const resolvedHeight = height ?? meta.defaultHeight;

  const illustrationProps: IllustrationProps = {
    className: `help-illustration ${className ?? ''}`.trim(),
    width: resolvedWidth,
    height: resolvedHeight,
    ariaLabel: meta.description,
  };

  return (
    <figure
      className="help-illustration-container"
      role="figure"
      aria-label={meta.title}
    >
      <BlockStack gap="200">
        <Box
          background="bg-surface"
          padding="400"
          borderRadius="200"
          borderColor="border"
          borderWidth="025"
        >
          <Suspense
            fallback={
              <IllustrationSkeleton
                width={
                  typeof resolvedWidth === 'number'
                    ? resolvedWidth
                    : meta.defaultWidth
                }
                height={
                  typeof resolvedHeight === 'number'
                    ? resolvedHeight
                    : meta.defaultHeight
                }
              />
            }
          >
            <div className="help-illustration-wrapper">
              <Illustration {...illustrationProps} />
            </div>
          </Suspense>
        </Box>
        {(caption || meta.title) && (
          <figcaption className="help-illustration-caption">
            <Text tone="subdued" variant="bodySm" as="span">
              {caption ?? meta.title}
            </Text>
          </figcaption>
        )}
      </BlockStack>
    </figure>
  );
}

export default HelpIllustrationEmbed;
